import React, { useState, useRef, useEffect } from 'react';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonProgressBar,
  IonToast,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonText,
  IonButtons,
  IonAlert,
  IonActionSheet,
  useIonAlert
} from '@ionic/react';
import {
  cameraOutline,
  trashOutline,
  eyeOutline,
  eyeOffOutline,
  peopleOutline,
  lockClosedOutline,
  imagesOutline,
  addOutline
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { ProfilePictureService } from '../services/profilePictureService';
import { CameraService } from '../services/cameraService';
import { PlatformService } from '../services/platform';
import Avatar from './Avatar';

interface ProfilePictureUploadProps {
  user: {
    id: string;
    full_name?: string;
    email?: string;
    profile_picture_url?: string;
    profile_picture_visibility?: 'public' | 'private' | 'none';
  };
  onUpdate?: (pictureUrl?: string, visibility?: 'public' | 'private' | 'none') => void;
  className?: string;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  user,
  onUpdate,
  className = ''
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [presentAlert] = useIonAlert();
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(false);

  // Check camera availability on component mount
  useEffect(() => {
    const checkCamera = async () => {
      const available = await CameraService.isCameraAvailable();
      setCameraAvailable(available);
    };
    checkCamera();
  }, []);

  const showMessage = (message: string, color: 'success' | 'danger' = 'success') => {
    setToastMessage(message);
    setToastColor(color);
    setShowToast(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const result = await ProfilePictureService.uploadProfilePicture(
        user.id,
        file,
        user.profile_picture_visibility || 'private'
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        showMessage(t('profile.picture.uploadSuccess', 'Profile picture uploaded successfully!'));
        onUpdate?.(result.url, user.profile_picture_visibility);
      } else {
        showMessage(result.error || t('profile.picture.uploadError', 'Failed to upload profile picture'), 'danger');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showMessage(t('profile.picture.uploadError', 'Failed to upload profile picture'), 'danger');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCameraCapture = async () => {
    try {
      // Use direct camera method for instant capture without editing
      const result = await CameraService.takeDirectCameraPhoto();
      
      if (result.success && result.file) {
        await handleUpload(result.file);
      } else if (result.error && !result.error.includes('cancelled')) {
        if (result.permissionDenied) {
          // Show alert to guide user to settings
          presentAlert({
            header: t('profile.picture.permissionRequired', 'Camera Permission Required'),
            message: t('profile.picture.permissionMessage', 'Camera permissions are needed to take photos. Would you like to open settings to enable them?'),
            buttons: [
              {
                text: t('common.cancel', 'Cancel'),
                role: 'cancel'
              },
              {
                text: t('profile.picture.openSettings', 'Open Settings'),
                handler: () => {
                  CameraService.openSettings();
                }
              }
            ]
          });
        } else {
          showMessage(result.error, 'danger');
        }
      }
    } catch (error) {
      console.error('Camera capture error:', error);
      showMessage(t('profile.picture.cameraError', 'Failed to capture photo'), 'danger');
    }
  };

  const handleGallerySelect = async () => {
    try {
      setShowActionSheet(false);
      
      const result = await CameraService.selectFromGallery();
      
      if (result.success && result.file) {
        await handleUpload(result.file);
      } else if (result.error && !result.error.includes('cancelled')) {
        if (result.permissionDenied) {
          // Show alert to guide user to settings
          presentAlert({
            header: t('profile.picture.permissionRequired', 'Gallery Permission Required'),
            message: t('profile.picture.galleryPermissionMessage', 'Photo library permissions are needed to select photos. Would you like to open settings to enable them?'),
            buttons: [
              {
                text: t('common.cancel', 'Cancel'),
                role: 'cancel'
              },
              {
                text: t('profile.picture.openSettings', 'Open Settings'),
                handler: () => {
                  CameraService.openSettings();
                }
              }
            ]
          });
        } else {
          showMessage(result.error, 'danger');
        }
      }
    } catch (error) {
      console.error('Gallery selection error:', error);
      showMessage(t('profile.picture.galleryError', 'Failed to select from gallery'), 'danger');
    }
  };

  const handlePhotoAction = () => {
    // On mobile with camera, show action sheet to choose camera or gallery
    if (PlatformService.isNative() && cameraAvailable) {
      setShowActionSheet(true);
    } else {
      // On web or when camera not available, fallback to file input
      fileInputRef.current?.click();
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    
    try {
      const result = await ProfilePictureService.deleteProfilePicture(user.id);
      
      if (result.success) {
        showMessage(t('profile.picture.deleteSuccess', 'Profile picture deleted successfully!'));
        onUpdate?.(undefined, 'none');
      } else {
        showMessage(result.error || t('profile.picture.deleteError', 'Failed to delete profile picture'), 'danger');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showMessage(t('profile.picture.deleteError', 'Failed to delete profile picture'), 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleVisibilityChange = async (visibility: 'public' | 'private' | 'none') => {
    setUpdatingVisibility(true);
    
    try {
      const result = await ProfilePictureService.updateProfilePictureVisibility(user.id, visibility);
      
      if (result.success) {
        showMessage(t('profile.picture.visibilityUpdateSuccess', 'Privacy setting updated successfully!'));
        onUpdate?.(user.profile_picture_url, visibility);
      } else {
        showMessage(result.error || t('profile.picture.visibilityUpdateError', 'Failed to update privacy setting'), 'danger');
      }
    } catch (error) {
      console.error('Visibility update error:', error);
      showMessage(t('profile.picture.visibilityUpdateError', 'Failed to update privacy setting'), 'danger');
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const getVisibilityIcon = (visibility?: string) => {
    switch (visibility) {
      case 'public':
        return peopleOutline;
      case 'private':
        return lockClosedOutline;
      case 'none':
        return eyeOffOutline;
      default:
        return eyeOutline;
    }
  };

  const getVisibilityDescription = (visibility?: string) => {
    switch (visibility) {
      case 'public':
        return t('profile.picture.visibility.public.description', 'Visible to everyone including future community features');
      case 'private':
        return t('profile.picture.visibility.private.description', 'Visible only to you and your coach/coachees');
      case 'none':
        return t('profile.picture.visibility.none.description', 'Hidden from everyone, shows monogram instead');
      default:
        return '';
    }
  };

  return (
    <IonCard className={className}>
      <IonCardHeader>
                        <IonCardTitle className="text-base sm:text-lg flex items-center">
          <IonIcon icon={cameraOutline} className="mr-2 flex-shrink-0" />
          {t('profile.picture.title', 'Profile Picture')}
        </IonCardTitle>
      </IonCardHeader>
      
      <IonCardContent>
        <div className="space-y-4">
          {/* Current Picture */}
          <div className="flex flex-col items-center space-y-4">
            <Avatar
              user={user}
              currentUserId={user.id}
              size="extra-large"
              showMonogramWhenNoImage={true}
            />
            
            {uploading && (
              <div className="w-full">
                <IonProgressBar value={uploadProgress / 100} />
                <IonText color="medium" className="text-sm text-center block mt-1">
                  {t('profile.picture.uploading', 'Uploading...')} {uploadProgress}%
                </IonText>
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <IonButton
              expand="block"
              fill="outline"
              onClick={handlePhotoAction}
              disabled={uploading}
            >
              <IonIcon icon={PlatformService.isNative() && cameraAvailable ? addOutline : cameraOutline} slot="start" />
              {user.profile_picture_url
                ? t('profile.picture.change', 'Change Picture')
                : (PlatformService.isNative() && cameraAvailable 
                    ? t('profile.picture.add', 'Add Picture')
                    : t('profile.picture.upload', 'Upload Picture')
                  )
              }
            </IonButton>

            {user.profile_picture_url && (
              <IonButton
                expand="block"
                fill="clear"
                color="danger"
                onClick={() => setShowDeleteAlert(true)}
                disabled={uploading}
              >
                <IonIcon icon={trashOutline} slot="start" />
                {t('profile.picture.remove', 'Remove Picture')}
              </IonButton>
            )}
          </div>

          {/* Privacy Settings */}
          <div className="space-y-2">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <IonIcon 
                icon={getVisibilityIcon(user.profile_picture_visibility)} 
                className="text-base flex-shrink-0"
              />
              <div className="flex-1">
                <div className="text-sm font-normal mb-1">
                  {t('profile.picture.visibility.label', 'Privacy Setting')}
                </div>
                <IonSelect
                  value={user.profile_picture_visibility || 'private'}
                  onIonChange={(e) => handleVisibilityChange(e.detail.value)}
                  disabled={updatingVisibility || uploading}
                  aria-label={t('profile.picture.visibility.label', 'Privacy Setting')}
                  className="text-sm"
                  interface="popover"
                >
                  <IonSelectOption value="private">
                    {t('profile.picture.visibility.private.label', 'Private')}
                  </IonSelectOption>
                  <IonSelectOption value="public">
                    {t('profile.picture.visibility.public.label', 'Public')}
                  </IonSelectOption>
                  <IonSelectOption value="none">
                    {t('profile.picture.visibility.none.label', 'Hidden')}
                  </IonSelectOption>
                </IonSelect>
              </div>
            </div>
            
            <IonText color="medium" className="text-xs">
              {getVisibilityDescription(user.profile_picture_visibility)}
            </IonText>
          </div>

          {/* Guidelines */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <IonText color="medium" className="text-xs">
              <strong>{t('profile.picture.guidelines.title', 'Guidelines:')}</strong>
              <ul className="mt-1 space-y-1">
                <li>• {t('profile.picture.guidelines.size', 'Maximum file size: 5MB')}</li>
                <li>• {t('profile.picture.guidelines.format', 'Formats: JPEG, PNG, WebP, GIF')}</li>
                <li>• {t('profile.picture.guidelines.content', 'Keep it professional and appropriate')}</li>
              </ul>
            </IonText>
          </div>
        </div>

        {/* Delete Confirmation */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header={t('profile.picture.deleteConfirm.title', 'Delete Profile Picture')}
          message={t('profile.picture.deleteConfirm.message', 'Are you sure you want to delete your profile picture? This action cannot be undone.')}
          buttons={[
            {
              text: t('common.cancel', 'Cancel'),
              role: 'cancel'
            },
            {
              text: t('common.delete', 'Delete'),
              role: 'destructive',
              handler: handleDelete
            }
          ]}
        />

        {/* Action Sheet for Camera/Gallery Selection */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header={t('profile.picture.selectSource', 'Select Photo Source')}
          buttons={[
            {
              text: t('profile.picture.camera', 'Take Photo'),
              icon: cameraOutline,
              handler: handleCameraCapture
            },
            {
              text: t('profile.picture.gallery', 'Choose from Gallery'),
              icon: imagesOutline,
              handler: handleGallerySelect
            },
            {
              text: t('common.cancel', 'Cancel'),
              role: 'cancel'
            }
          ]}
        />

        {/* Toast Messages */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
        />
      </IonCardContent>
    </IonCard>
  );
};

export default ProfilePictureUpload; 